import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { useIntl } from '@/intl'
import {
  ActionButtons,
  CarCardBody,
  CarCardFooter,
  CarCardFooterSection,
  CarItem,
  CarRentalCard,
  Price,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption'
import {
  carIsBeingUpgraded,
  carToBeUpgraded,
  editingCar,
  useCarRentalServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { addCarToOptionAndUpgradeCar } from '@/scopes/checkout/domain/actions'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useGetAllCarsQuery, useSelectCar } from '@/scopes/checkout/filterableCars/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Button } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'

export function CarView({ ...car }: FilterableCarItem) {
  return (
    <CarItem key={car.id} $selected={false}>
      <CarCardView {...car} />
    </CarItem>
  )
}

export function CarCardView({ ...option }: FilterableCarItem) {
  const { value, id, price } = option
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const { selectCar } = useSelectCar()
  const { startEditingCar, setCarToBeSelected, setShowFilterableCars, setShowExtraCars, setCarIsBeingUpgraded, setCarToBeUpgraded, endEditingCar } = useCarRentalServiceStore()
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded)
  const carValueToBeUpgraded = useCarRentalServiceStore(carToBeUpgraded)
  const { data: allCarsData } = useGetAllCarsQuery()

  const currentEditingCar = useCarRentalServiceStore(editingCar(carValueToBeUpgraded))
  const intl = useIntl()

  const handleSelectClick = React.useCallback(async () => {
    if (!id)
      return
    if (!service)
      return
    const cars = allCarsData?.cars ?? {}

    selectCar(id)
    if (isCarBeingUpgraded) {
      if (currentEditingCar) {
        setServiceValue(addCarToOptionAndUpgradeCar({ value, participantId: currentEditingCar.driverId ?? '', id })(service, cars))
      }
      setCarIsBeingUpgraded(false)
      setCarToBeUpgraded('')
      endEditingCar(id)
      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: value,
          description: 'upgrade button clicked',
          element: 'button',
        }),
      )
    }
    else {
      setCarToBeSelected(id)
      setShowExtraCars(false)
      setShowFilterableCars(false)
      startEditingCar(id, {
        driverId: null,
        driverBirthDate: new Date(),
        driverHasCreditCard: false,
        bookedEquipmentsWithQuantity: [],
        isEditing: false,
      })
      sendEvent(
        createCarEvent({
          result: 'Car selected',
          type: value,
          description: 'Filtered car selected',
          element: 'button',
          validated: true,
        }),
      )
    }
  }, [allCarsData, currentEditingCar, endEditingCar, isCarBeingUpgraded, service, setCarIsBeingUpgraded, setCarToBeUpgraded, setServiceValue, selectCar, setShowFilterableCars, sendEvent, setCarToBeSelected, setShowExtraCars, startEditingCar, value, id])

  if (!service)
    return
  return (
    <CarRentalCard>
      <CarCardBody {...option} />
      <CarCardFooterSection>
        <CarCardFooter>
          <Price as="span" $variant="body-m-bold-tight">
            {intl.formatTruncatedCurrency(price)}
          </Price>
          <ActionButtons>
            <Button $type="secondary" onClick={handleSelectClick} $size="S">
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text' })}
            </Button>
          </ActionButtons>
        </CarCardFooter>
      </CarCardFooterSection>
    </CarRentalCard>
  )
}
